import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { GearSixIcon, VaultIcon } from "@phosphor-icons/react";
import {
  AppShellContent,
  AppShellMain
} from "@/zapi-frontend/pages/ProjectDetailsPage/ui/ProjectLayout/ui/blocks/AppShell";
import {
  FrameCompleteness,
  FrameSheet,
  type FrameSection
} from "@/zapi-frontend/pages/CreateProjectPage/ui/blocks/CaseFrame";
import { BriefCard } from "@/zapi-frontend/pages/CreateProjectPage/ui/blocks/ProjectBrief";
import {
  ProjectCompactBar,
  ProjectDetails,
  type ProjectPicture,
  type ProjectTag,
  type ProjectTagDefinition
} from "@/zapi-frontend/pages/CreateProjectPage/ui/blocks/ProjectDetails";
import {
  SetupAnalysisSettings,
  SetupLaunchButton,
  SetupLaunchDialog,
  SetupPanel,
  SetupSurface,
  SetupVaultSheet,
  type SetupAnalysisSettingsValue,
  type SetupSource
} from "@/zapi-frontend/widgets/CreateProjectChat/ui/blocks/ProjectSetup";
import { AmbientField } from "@libs/zapi-ui/ds/components/ambient-field";
import { Badge } from "@libs/zapi-ui/ds/components/badge";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Reveal } from "@libs/zapi-ui/ds/components/reveal";
import { Skeleton } from "@libs/zapi-ui/ds/components/skeleton";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { TooltipProvider } from "@libs/zapi-ui/ds/components/tooltip";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import type { CaseBriefCompleteness } from "@/zapi-frontend/widgets/CreateProjectChat";
import type { TBriefStatus } from "@/zapi-frontend/widgets/CreateProjectChat/ui/ProjectCreationHeader";
import type { TPreUploadedDocument } from "@/zapi-frontend/pages/CreateProjectPage/model/usePreUploadDocuments";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";
import { removeTimestampPrefix } from "@/zapi-frontend/shared/lib/splitFileName";
import {
  ACCEPTED_PHOTO_TYPES,
  MAX_PHOTO_SIZE_BYTES,
  readAsDataUrl
} from "@/zapi-frontend/shared/ui/ProjectIconEditor/ProjectIconEditor";
import {
  caseFrameSections,
  isTemplateOnly,
  sectionFilled,
  unansweredPrompts,
  unconfirmedItems,
  withSectionBody
} from "../../lib/caseFrameSections";
import { CaseFrameSectionEditor } from "./CaseFrameSectionEditor";
import { useNewProjectLabels } from "./useNewProjectLabels";

// The page enters top-down: the project card first, then the case frame.
const CASE_FRAME_REVEAL_DELAY_MS = 180;
const READY_TO_LAUNCH = "complete";

type TLanguageOption = { label: string; value: string };

export type TNewProjectViewProps = {
  readOnly: boolean;
  restoring: boolean;
  chat: ReactNode;
  project: {
    name: string;
    description: string;
    pending: boolean;
    onNameChange: (name: string) => void;
    onDescriptionChange: (description: string) => void;
    picture: { url: string; credit?: string | null } | null;
    // Only a picture the user uploaded can be removed; a cover photo found for the project stays.
    pictureRemovable: boolean;
    onPictureChange: (dataUrl: string | null) => void;
    language: string;
    languageOptions: TLanguageOption[];
    onLanguageChange: (language: string) => void;
    // Empty until the user names the version; the card then shows the first version's default name.
    versionName: string;
    onVersionNameChange: (name: string) => void;
    visibility: "organization" | "private";
    onVisibilityChange: (visibility: "organization" | "private") => void;
    dimensions: TOrganizationProjectDimension[];
    dimensionValues: Record<string, string>;
    onDimensionChange: (dimensionId: number, value: string) => void;
    // Organization admins also create, edit and delete the organization's tags from the card.
    tagManagement?: {
      onCreate: (definition: Omit<ProjectTagDefinition, "id">) => void;
      onUpdate: (definitionId: string, definition: Omit<ProjectTagDefinition, "id">) => void;
      onDelete: (definitionId: string) => void;
    };
  };
  vault: {
    documents: TPreUploadedDocument[];
    pendingNames: string[];
    onAddFiles: (files: File[]) => void;
    onAddLink: (url: string) => void;
    onRemove: (documentId: number) => void;
    // Documents Leo hasn't read yet; they show as being read until it has.
    unreadIds: number[];
  };
  frame: {
    brief: string;
    onChange: (brief: string) => void;
    completeness: CaseBriefCompleteness | null;
    progress: number;
    activeSection: number | null;
    writingSection: number | null;
    coveredUpTo: number | null;
    showTemplatePrompts: boolean;
    caseBriefId: number | null;
    lang: string;
  };
  launch: {
    status: TBriefStatus;
    decisionPending: boolean;
    isLaunching: boolean;
    confirmOpen: boolean;
    onConfirmOpenChange: (open: boolean) => void;
    onLaunch: () => void;
  };
  // Labs users only. Organizations still on templates keep the settings dialog with the template picker.
  settings?:
    | {
        models: { value: string; label: string }[];
        value: SetupAnalysisSettingsValue;
        onSave: (value: SetupAnalysisSettingsValue) => void;
      }
    | { onOpenLegacy: () => void };
};

function toTags(
  dimensions: TOrganizationProjectDimension[],
  values: Record<string, string>
): { library: ProjectTagDefinition[]; tags: ProjectTag[] } {
  const library = dimensions.map((dimension) => ({
    id: String(dimension.id),
    name: dimension.name,
    type: dimension.type,
    options: dimension.options
  }));
  const tags = library.flatMap((definition) => {
    const value = values[definition.id]?.trim();
    return value ? [{ ...definition, value }] : [];
  });
  return { library, tags };
}

export function NewProjectView({
  readOnly,
  restoring,
  chat,
  project,
  vault,
  frame,
  launch,
  settings
}: TNewProjectViewProps) {
  const { t } = useTranslation(["createProject", "projectHome"]);
  const labels = useNewProjectLabels();
  const [vaultOpen, setVaultOpen] = useState(false);

  // The case frame scrolls inside its own card; once it does, the project card folds into a one-line bar. Scrolling
  // back to the top of the frame keeps it folded; a second scroll up from there, or a click on the bar, unfolds it.
  const frameScroll = useRef<HTMLDivElement>(null);
  const [projectFolded, setProjectFolded] = useState(false);
  const lastWheel = useRef({ at: 0, delta: 0 });
  const reachedTopAt = useRef<number | null>(null);
  const unfolding = useRef(false);
  const unfoldOnSecondPull = (deltaY: number) => {
    const now = performance.now();
    const previous = lastWheel.current;
    const newGesture =
      now - previous.at > 200 || (Math.abs(deltaY) > 8 && Math.abs(deltaY) > Math.abs(previous.delta) * 1.6);
    lastWheel.current = { at: now, delta: deltaY };
    const atTopBefore = reachedTopAt.current !== null && now - reachedTopAt.current > 250;
    if (deltaY < 0 && newGesture && atTopBefore && projectFolded) setProjectFolded(false);
  };
  const [frameMoreBelow, setFrameMoreBelow] = useState(false);
  useEffect(() => {
    const scroller = frameScroll.current;
    const content = scroller?.firstElementChild;
    if (!scroller || !content) return;
    const measure = () => setFrameMoreBelow(scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight > 4);
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    observer.observe(content);
    return () => observer.disconnect();
  }, [restoring]);

  // The page itself doesn't scroll, so a wheel anywhere on it moves the case frame instead, except over Leo or
  // something that scrolls on its own.
  const unfoldRef = useRef(unfoldOnSecondPull);
  useEffect(() => {
    unfoldRef.current = unfoldOnSecondPull;
  });
  useEffect(() => {
    const scrollFrameFromPage = (event: WheelEvent) => {
      const scroller = frameScroll.current;
      const target = event.target as HTMLElement;
      if (!scroller || scroller.contains(target)) return;
      if (
        target.closest(
          "[data-slot=leo-panel], [data-slot=leo-overlay], [role=dialog], [role=listbox], [role=menu], [data-slot=popover-content], textarea"
        )
      )
        return;
      event.preventDefault();
      scroller.scrollBy({ top: event.deltaY });
      unfoldRef.current(event.deltaY);
    };
    window.addEventListener("wheel", scrollFrameFromPage, { passive: false });
    return () => window.removeEventListener("wheel", scrollFrameFromPage);
  }, []);

  const { library, tags } = useMemo(
    () => toTags(project.dimensions, project.dimensionValues),
    [project.dimensions, project.dimensionValues]
  );
  // The card gives a tag it adds an id of its own and knows the organization's tags by name.
  const changeTags = (next: ProjectTag[]) => {
    const valueByName = new Map(next.map((tag) => [tag.name.toLowerCase(), tag.value ?? ""]));
    for (const definition of library) {
      const before = project.dimensionValues[definition.id] ?? "";
      const after = valueByName.get(definition.name.toLowerCase()) ?? "";
      if (before !== after) project.onDimensionChange(Number(definition.id), after);
    }
  };

  const uploadPicture = async (file: File) => {
    if (!ACCEPTED_PHOTO_TYPES.includes(file.type)) {
      toast.error(t("projectHome:projectSetup.photoInvalidType"));
      return;
    }
    if (file.size > MAX_PHOTO_SIZE_BYTES) {
      toast.error(t("projectHome:projectSetup.photoTooLarge"));
      return;
    }
    try {
      project.onPictureChange(await readAsDataUrl(file));
    } catch {
      toast.error(t("projectHome:projectSetup.photoUploadFailed"));
    }
  };
  const picture: ProjectPicture | null = project.picture
    ? { url: project.picture.url, alt: project.name, credit: project.picture.credit ?? undefined }
    : null;

  const languageLabel = (value: string) =>
    project.languageOptions.find((option) => option.value === value)?.label ?? value;
  const languageValue = (label: string) =>
    project.languageOptions.find((option) => option.label === label)?.value ?? label;

  const sources: SetupSource[] = [
    ...vault.documents.map((doc) => ({
      id: String(doc.documentId),
      kind: doc.webUrl ? ("link" as const) : ("file" as const),
      name: doc.webUrl ?? removeTimestampPrefix(doc.fileName),
      status: vault.unreadIds.includes(doc.documentId) ? ("reading" as const) : ("done" as const)
    })),
    ...vault.pendingNames.map((name, index) => ({
      id: `pending-${index}-${name}`,
      kind: /^https?:\/\//i.test(name) ? ("link" as const) : ("file" as const),
      name,
      status: "reading" as const,
      removable: false
    }))
  ];

  const briefSections = caseFrameSections(frame.brief);
  const lastSection = briefSections[briefSections.length - 1]?.number ?? null;
  // Once a read starts Leo's walk through the frame, only the sections behind it can be settled; without one,
  // a filled section is settled unless the decision question is still open on it.
  const behindLeo = (number: number) =>
    frame.coveredUpTo !== null
      ? number < frame.coveredUpTo
      : frame.activeSection === null || number < frame.activeSection;
  const openCounts: Record<number, number> = {};
  const attention: number[] = [];
  const frameSections: FrameSection[] = briefSections.map((section) => {
    const writing = section.number === frame.writingSection;
    const written = !isTemplateOnly(section.body);
    const active = section.number === frame.activeSection;
    // Sections Leo hasn't gone through yet look untouched, even when the read already wrote them.
    const ahead = frame.coveredUpTo !== null && section.number > frame.coveredUpTo;
    const open = written ? unconfirmedItems(section.body) + unansweredPrompts(section.body) : 0;
    openCounts[section.number] = open;
    const settled =
      written && open === 0 && sectionFilled(frame.completeness, section.number) && behindLeo(section.number);
    if (written && !settled && frame.coveredUpTo !== null && section.number < frame.coveredUpTo)
      attention.push(section.number);
    return {
      id: section.number,
      label: section.title,
      hint: "",
      state: writing ? "writing" : active || (written && !ahead) ? "done" : "pending",
      settled,
      claims: [],
      // A section Leo is writing for the first time shows the design's writing placeholder until text arrives.
      content:
        writing && !written ? undefined : (
          <CaseFrameSectionEditor
            body={section.body}
            onChange={(body) => frame.onChange(withSectionBody(frame.brief, section.number, body))}
            placeholder={labels.sectionPlaceholder}
            showTemplatePrompts={frame.showTemplatePrompts}
            readOnly={readOnly}
            caseBriefId={frame.caseBriefId}
          />
        )
    };
  });

  // While Leo is on the last section the frame follows it to the end, and goes there once Leo is through.
  const onLastSection =
    lastSection !== null && (frame.activeSection ?? frame.writingSection) === lastSection && !restoring;
  const finished = lastSection !== null && frame.coveredUpTo !== null && frame.coveredUpTo > lastSection;
  useEffect(() => {
    const scroller = frameScroll.current;
    const content = scroller?.firstElementChild;
    if (!(onLastSection || finished) || !scroller || !content) return;
    const toEnd = () => scroller.scrollTo({ top: scroller.scrollHeight, behavior: "smooth" });
    toEnd();
    if (!onLastSection) return;
    const observer = new ResizeObserver(toEnd);
    observer.observe(content);
    return () => observer.disconnect();
  }, [onLastSection, finished]);

  const launchBlockedReason = launch.decisionPending
    ? labels.launchBlockedDecision
    : launch.status === "empty"
    ? labels.launchBlockedName
    : undefined;

  const details = [
    languageLabel(project.language),
    project.versionName || labels.versionInitial,
    project.visibility === "private" ? labels.private : labels.organization
  ];

  // z-40 lifts the page above the app header, which lives outside it, so Leo's overlay covers the header.
  // The fixed canvas behind it fills the strip a shorter header leaves at the bottom on narrow screens.
  return (
    <TooltipProvider>
      <div aria-hidden="true" className="fixed inset-0 -z-10 bg-canvas" />
      <div className="relative isolate z-40 flex min-h-[calc(100svh-var(--app-header-height))] flex-col bg-canvas text-foreground">
        <AmbientField className="fixed inset-0 -z-10" />
        <div className="flex flex-1 lg:pe-4">
          <AppShellMain className="min-w-0 pt-2 sm:px-8 lg:px-12 xl:pb-0">
            <AppShellContent size="wide">
              <SetupPanel
                title={labels.title}
                tools={
                  readOnly ? null : (
                    <>
                      {settings &&
                        ("onOpenLegacy" in settings ? (
                          <Button
                            variant="outline"
                            size="icon-lg"
                            shape="pill"
                            aria-label={labels.setup.analysisSettings}
                            onClick={settings.onOpenLegacy}
                            data-testid="project-creation-header-settings-button"
                          >
                            <GearSixIcon />
                          </Button>
                        ) : (
                          <SetupAnalysisSettings
                            models={settings.models}
                            value={settings.value}
                            onSave={settings.onSave}
                            labels={labels.setup}
                          />
                        ))}
                      <Button
                        variant="outline"
                        size="lg"
                        shape="pill"
                        disabled={restoring}
                        onClick={() => setVaultOpen(true)}
                        data-testid="project-setup-vault-button"
                      >
                        <VaultIcon data-icon="inline-start" />
                        {labels.vault}
                        <Badge variant="secondary" className="tabular-nums">
                          {restoring ? (
                            <Spinner aria-label={t("newProject.loading")} className="size-3" />
                          ) : (
                            sources.length
                          )}
                        </Badge>
                      </Button>
                    </>
                  )
                }
                action={
                  readOnly ? null : (
                    <SetupLaunchButton
                      blockedReason={restoring ? labels.launchBlockedName : launchBlockedReason}
                      pending={launch.isLaunching}
                      onLaunch={() => launch.onConfirmOpenChange(true)}
                      labels={labels.setup}
                    />
                  )
                }
                className="h-[calc(100svh-var(--app-header-height)-8rem)] gap-4 xl:h-[calc(100svh-var(--app-header-height)-1.25rem)]"
              >
                <div className="flex min-h-0 flex-1 flex-col">
                  <div className="flex min-h-0 flex-1 flex-col pb-1">
                    <div
                      ref={(node) => {
                        if (node) node.inert = projectFolded;
                      }}
                      className={cn(
                        "grid transition-[grid-template-rows] duration-[520ms] ease-(--vt-ease-morph)",
                        projectFolded ? "grid-rows-[0fr]" : "grid-rows-[1fr]"
                      )}
                    >
                      <div
                        className={cn(
                          "-m-1 min-h-0 origin-top overflow-hidden p-1 transition-[opacity,scale,filter]",
                          projectFolded
                            ? "scale-[0.98] opacity-0 blur-[2px] duration-200 ease-(--vt-ease-exit)"
                            : "blur-0 scale-100 opacity-100 delay-150 duration-[380ms] ease-(--vt-ease-enter)"
                        )}
                      >
                        <Reveal>
                          <SetupSurface className="flex-none p-5 ring-foreground/5 sm:p-5">
                            <ProjectDetails
                              pending={restoring || project.pending}
                              values={{
                                name: project.name,
                                description: project.description,
                                picture,
                                language: languageLabel(project.language),
                                versionName: project.versionName || labels.versionInitial,
                                dimensions: {},
                                tags
                              }}
                              languages={project.languageOptions.map((option) => option.label)}
                              visibility={project.visibility}
                              onVisibilityChange={project.onVisibilityChange}
                              onNameChange={project.onNameChange}
                              onDescriptionChange={project.onDescriptionChange}
                              onLanguageChange={(label) => project.onLanguageChange(languageValue(label))}
                              onVersionNameChange={project.onVersionNameChange}
                              onTagsChange={changeTags}
                              tagLibrary={library}
                              onCreateTagDefinition={project.tagManagement?.onCreate}
                              onUpdateTagDefinition={project.tagManagement?.onUpdate}
                              onDeleteTagDefinition={project.tagManagement?.onDelete}
                              canManageTags={!!project.tagManagement}
                              onUploadPicture={uploadPicture}
                              pictureAccept={ACCEPTED_PHOTO_TYPES.join(",")}
                              onRemovePicture={
                                project.pictureRemovable ? () => project.onPictureChange(null) : undefined
                              }
                              readOnly={readOnly}
                              labels={labels.projectDetails}
                            />
                          </SetupSurface>
                        </Reveal>
                      </div>
                    </div>
                    <div
                      className={cn(
                        "grid transition-[grid-template-rows] duration-[520ms] ease-(--vt-ease-morph)",
                        projectFolded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                      )}
                    >
                      <div className="-m-1 min-h-0 overflow-hidden p-1">
                        <ProjectCompactBar
                          picture={picture}
                          name={project.name || labels.untitled}
                          details={details}
                          visible={projectFolded}
                          labels={labels.projectDetails}
                          onExpand={() => {
                            const scroller = frameScroll.current;
                            if (!scroller || scroller.scrollTop <= 1) return setProjectFolded(false);
                            unfolding.current = true;
                            scroller.scrollTo({ top: 0, behavior: "smooth" });
                            window.setTimeout(() => {
                              if (!unfolding.current) return;
                              unfolding.current = false;
                              scroller.scrollTop = 0;
                              setProjectFolded(false);
                            }, 900);
                          }}
                        />
                      </div>
                    </div>
                    <Reveal delay={CASE_FRAME_REVEAL_DELAY_MS} className="mt-4 flex min-h-0 flex-1 flex-col">
                      <BriefCard
                        title={labels.caseFrameTitle}
                        heading="section"
                        className="min-h-0 flex-1 pb-2"
                        aside={
                          <FrameCompleteness
                            sections={frameSections}
                            settled={frameSections.filter((section) => section.settled).map((section) => section.id)}
                            attention={attention}
                            activeId={frame.activeSection ?? undefined}
                            percent={Math.round(frame.progress)}
                            labels={labels.caseFrame}
                          />
                        }
                      >
                        <div
                          ref={frameScroll}
                          lang={frame.lang}
                          data-scrolled={projectFolded || undefined}
                          data-more-below={frameMoreBelow || undefined}
                          onWheel={(event) => unfoldOnSecondPull(event.deltaY)}
                          onScroll={(event) => {
                            const scroller = event.currentTarget;
                            if (unfolding.current) {
                              if (scroller.scrollTop <= 1) {
                                unfolding.current = false;
                                setProjectFolded(false);
                              }
                            } else if (scroller.scrollTop > 8) setProjectFolded(true);
                            if (scroller.scrollTop > 0) reachedTopAt.current = null;
                            else reachedTopAt.current ??= performance.now();
                            setFrameMoreBelow(scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight > 4);
                          }}
                          className="-mx-5 min-h-0 flex-1 [scrollbar-width:none] overflow-y-auto overscroll-contain border-t border-transparent px-5 pt-3 transition-colors duration-300 data-more-below:[mask-image:linear-gradient(to_bottom,black_calc(100%-2.5rem),transparent)] data-scrolled:border-border [&::-webkit-scrollbar]:hidden"
                        >
                          {restoring ? (
                            <div className="flex flex-col gap-3 py-3">
                              <Skeleton className="h-5 w-2/3" />
                              <Skeleton className="h-5 w-1/2" />
                              <Skeleton className="h-5 w-3/5" />
                            </div>
                          ) : (
                            <FrameSheet
                              sections={frameSections}
                              activeId={frame.activeSection ?? undefined}
                              openCounts={openCounts}
                              emptyText=""
                              onEditClaim={() => undefined}
                              labels={labels.caseFrame}
                            />
                          )}
                        </div>
                      </BriefCard>
                    </Reveal>
                  </div>
                </div>
              </SetupPanel>
            </AppShellContent>
          </AppShellMain>
          {chat}
        </div>

        <SetupVaultSheet
          open={vaultOpen}
          onOpenChange={setVaultOpen}
          sources={sources}
          onRemove={
            readOnly
              ? undefined
              : (id) => {
                  if (!id.startsWith("pending-")) vault.onRemove(Number(id));
                }
          }
          onAddFiles={readOnly ? undefined : vault.onAddFiles}
          onAddLink={readOnly ? undefined : vault.onAddLink}
          labels={labels.setup}
        />

        <SetupLaunchDialog
          open={launch.confirmOpen}
          ready={launch.status === READY_TO_LAUNCH || finished}
          onOpenChange={launch.onConfirmOpenChange}
          onLaunch={launch.onLaunch}
          labels={labels.setup}
        />
      </div>
    </TooltipProvider>
  );
}
